import { type ComponentProps, useContext, useId, useRef } from "react";

import {
  Box,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  CloseIcon,
  IconButton,
  Portal,
  Row,
  SelectorIcon,
  useDisclosure,
} from "@hightouch/htui";
import { type ExtendedOption, toExtendedOption } from "@hightouch/formkit";
import { useFlags } from "launchdarkly-react-client-sdk";

import {
  type JsonColumnProps,
  type Mapping,
  MappingType,
  type OverwriteStandardInputWithArrayProps,
  type Props,
  type SelectedOptionContext,
} from "src/formkit/components/types";
import { LegacyMapper } from "src/formkit/components/mapper-v2/legacy-mapper";
import { MapperPopoverContent } from "src/formkit/components/mapper-v2/mapper-popover-content";
import {
  getEnabledValueTypes,
  type MapperValue,
} from "src/formkit/components/mapper-v2/mapper-selection";
import { Value } from "src/formkit/components/mapper-v2/mapper-value";
import { isValueEmpty } from "src/formkit/components/mapper-v2/utils";
import { useFormkitContext } from "src/formkit/components/formkit-context";
import { TemplatePreviewContext } from "./template-preview-context";

type MapperProps = ComponentProps<typeof LegacyMapper>;

/** The mapping value picker. The redesigned popover launches with custom audience traits, so their `enableCustomAudienceTraits` flag swaps it in. */
export function Mapper({ popoverMaxWidth, ...props }: MapperProps) {
  const { enableCustomAudienceTraits } = useFlags();
  return enableCustomAudienceTraits ? (
    <RedesignedMapper {...props} />
  ) : (
    <LegacyMapper popoverMaxWidth={popoverMaxWidth} {...props} />
  );
}

type RedesignedMapperProps = Readonly<
  Props & {
    enableInLineMapper?: boolean;
    isClearable?: boolean;
    jsonColumnProperties: JsonColumnProps;
    mappingTypes?: MappingType[];
    onChangeJsonColumnProperties: React.Dispatch<
      React.SetStateAction<JsonColumnProps>
    >;
    onReloadEligibleInlineMapperColumns: (
      currentSelectedColumn?: string,
    ) => void;
    parentMapping?: Mapping;
    popoverPlacement?: ComponentProps<typeof Popover>["placement"];
    selectedOption: SelectedOptionContext | ExtendedOption | undefined;
  } & OverwriteStandardInputWithArrayProps
>;

function RedesignedMapper({
  columnOptions,
  enableInLineMapper,
  isClearable,
  isDisabled = false,
  isError,
  jsonColumnProperties,
  mappingTypes: mappingTypesProp,
  onChange,
  onChangeJsonColumnProperties,
  onReloadEligibleInlineMapperColumns,
  overwriteColumnsWithArrayProps,
  parentMapping,
  placeholder,
  popoverPlacement = "bottom-start",
  selectedOption,
  templates,
  ...props
}: RedesignedMapperProps) {
  const { isOpen, onClose, onToggle } = useDisclosure();
  const focusRef = useRef<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const valueId = useId();
  const { model, mappingTypesOverride } = useFormkitContext();
  const templatePreviewCtx = useContext(TemplatePreviewContext);

  const committedValue: MapperValue = props.value || {};
  const triggerValue = {
    ...committedValue,
    type: committedValue.type ?? MappingType.STANDARD,
  };
  const isFieldDisabled =
    isDisabled || props.value?.from?.type === MappingType.BOOSTED;
  const canClear =
    isClearable && !isFieldDisabled && !isValueEmpty(triggerValue);

  const enabledTypes = getEnabledValueTypes({
    mappingTypesOverride,
    fieldMappingTypes: mappingTypesProp,
    hasModel: Boolean(model),
    enableInLineMapper,
    destinationFieldType: toExtendedOption(selectedOption)?.extendedType?.type,
  });

  // Chakra stops returning focus once it has moved between controls inside the popover, so the mapper returns it itself.
  const closePopover = () => {
    const focused = document.activeElement;
    const isFocusInPopover =
      !focused ||
      focused === document.body ||
      Boolean(contentRef.current?.contains(focused));
    onClose();
    if (isFocusInPopover) triggerRef.current?.focus({ preventScroll: true });
  };

  // The clear button unmounts once the value is empty, so focus moves to the trigger instead of being lost.
  const clearValue = () => {
    onChange(undefined);
    triggerRef.current?.focus();
  };

  const borderColor = isFieldDisabled
    ? "base.borderDisabled"
    : isError
      ? "danger.base"
      : isOpen
        ? "primary.base"
        : "base.border";

  return (
    <Row align="center" gap={1.5}>
      <Box flex={1} minWidth={0} position="relative">
        <Popover
          isLazy
          preventOverflow
          closeOnBlur={!templatePreviewCtx?.isPreviewOpen}
          initialFocusRef={focusRef}
          isOpen={isOpen}
          returnFocusOnClose={false}
          onClose={closePopover}
          placement={popoverPlacement}
        >
          <PopoverTrigger>
            <Row
              ref={triggerRef}
              as="button"
              type="button"
              aria-label="Select mapping type"
              // Tooling finds the trigger by its name, so screen readers get the current mapping from the description.
              aria-describedby={valueId}
              disabled={isFieldDisabled}
              alignItems="center"
              bg={
                isFieldDisabled
                  ? "base.backgroundDisabled"
                  : isOpen
                    ? "white"
                    : "base.lightBackground"
              }
              borderWidth="hairline"
              borderStyle="solid"
              borderColor={borderColor}
              borderRadius="lg"
              boxShadow={isFieldDisabled ? "none" : "buttonShadowLight"}
              color={isFieldDisabled ? "text.disabled" : undefined}
              cursor={isFieldDisabled ? "not-allowed" : undefined}
              display="flex"
              justifyContent="space-between"
              minHeight={8}
              pl={2}
              // Keeps the value out from under the clear button.
              pr={canClear ? 8 : 2}
              py={1}
              transitionProperty="background-color, border-color"
              transitionDuration="fast"
              transitionTimingFunction="easeOutQuad"
              width="100%"
              _active={{
                bg: isFieldDisabled ? undefined : "white",
                borderColor: borderColor,
              }}
              _hover={{
                bg: isFieldDisabled ? undefined : "white",
                borderColor: borderColor,
              }}
              _focus={{ outline: "none" }}
              _focusVisible={{
                boxShadow: isFieldDisabled ? undefined : "outline",
              }}
              onClick={isFieldDisabled ? undefined : onToggle}
            >
              <Value
                id={valueId}
                isDisabled={isFieldDisabled}
                placeholder={placeholder}
                value={triggerValue}
                columnOptions={columnOptions}
                selectedOptionSemanticType={
                  toExtendedOption(selectedOption)?.extendedType
                    ?.semanticColumnType
                }
              />
              {!canClear && (
                <Box
                  as={SelectorIcon}
                  boxSize={5}
                  color={isFieldDisabled ? "text.disabled" : "text.secondary"}
                />
              )}
            </Row>
          </PopoverTrigger>
          <Portal>
            <PopoverContent
              ref={contentRef}
              borderRadius="3xl"
              boxShadow="md"
              height="lg"
              maxWidth="3xl"
              overflow="hidden"
              width="100vw"
            >
              <PopoverBody display="flex" height="100%" p={0}>
                <MapperPopoverContent
                  columnOptions={columnOptions}
                  enabledTypes={enabledTypes}
                  focusRef={focusRef}
                  jsonColumnProperties={jsonColumnProperties}
                  overwriteColumnsWithArrayProps={
                    overwriteColumnsWithArrayProps
                  }
                  parentMapping={parentMapping}
                  templates={templates}
                  value={committedValue}
                  onChangeJsonColumnProperties={onChangeJsonColumnProperties}
                  onClose={closePopover}
                  onCommit={onChange}
                  onReloadEligibleInlineMapperColumns={
                    onReloadEligibleInlineMapperColumns
                  }
                />
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
        {/* Beside the trigger rather than inside it, so it is its own tab stop and a click doesn't open the popover. */}
        {canClear && (
          <IconButton
            aria-label="Clear value"
            icon={CloseIcon}
            position="absolute"
            right={1}
            size="sm"
            top="50%"
            transform="translateY(-50%)"
            onClick={clearValue}
          />
        )}
      </Box>
    </Row>
  );
}
